import { Component, EventEmitter, Inject, OnInit, Output, ViewChild, computed, signal } from '@angular/core';
import { RxState } from '@rx-angular/state';
import { BehaviorSubject, Observable } from 'rxjs';
import { SearchbarCustomEvent } from '@ionic/angular';
import { GlobalState, GLOBAL_RX_STATE } from 'src/app/app-state.tokens';
import { HttpIoService } from 'src/app/services/communication/http-io.service';
import { MapPosition } from '../map/map.component';
import { StoreService } from 'src/app/services/utils/store.service';
import { _ } from 'src/app/consts';
import { TranslateService } from '@ngx-translate/core';
import { MainStateService } from 'src/app/services/state/app-main-state.service';
import { ToastService } from 'src/app/services/utils/toast.service';

const URL = "https://nominatim.eagleprojects.cloud/search?q="
const TOLLERANCE = 0.1;
const LOCALSTORAGE_MAX_LENGTH = 10;

export type Search = { name: string, title: string, subtitle: string, position: MapPosition, icon: string }
export interface SearchResult { osm_id: number; place_id: number; type: string; boundigbox: string[], lat: string, lon: string, importance: number, display_name: string }
/**
 * `Error` è il terzo esito che mancava. Il geocoder può rispondere «zero risultati» (`Search` con
 * lista vuota → messaggio NO_RESULT) oppure NON rispondere affatto — servizio giù, 500, timeout —
 * e i due casi non sono la stessa cosa per chi cerca: al primo cambi le parole, al secondo riprovi
 * più tardi. Prima il secondo caso non esisteva nel modello e finiva a schermo vuoto.
 */
export type ResultType = "History" | "Search" | "Error";

@Component({
    selector: 'yf-search-panel',
    templateUrl: './search-panel.component.html',
    styleUrls: ['./search-panel.component.scss'],
    standalone: false
})
export class SearchPanelComponent implements OnInit {

  @ViewChild("ionSearchBar") ionSearchBar: HTMLIonSearchbarElement;
  @Output() showPosition = new EventEmitter<MapPosition>();
  @Output() isFocusedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  visible$: Observable<boolean>;
  value: string;


  $results = signal<{ type: ResultType, entities: Search[] }>({ type: "History", entities: [] });

  /** Geocoder interrogato, risposta non ancora arrivata: il pannello lo dice invece di restare fermo. */
  $searching = signal(false);

  /**
   * Progressivo delle ricerche in volo. La searchbar ha `debounce=1000` ma il debounce non ordina
   * NIENTE: due richieste possono sovrapporsi e la più vecchia tornare per ultima, sovrascrivendo i
   * risultati di quella che l'utente sta guardando (e lasciando lo spinner acceso). Vince l'ultima
   * partita, le risposte sorpassate si scartano.
   */
  private searchSeq = 0;

  $height = computed(() => {
    let totalDimension = (this.$results().entities.length * 61);
    let items = this.$results().entities.length;
    return {
      height: items > 5 ? '330px' : `${totalDimension}px`,
      overflow: items > 5 ? "scroll" : "hidden"
    }
  });

  constructor(
    private HIO: HttpIoService,
    private storage: StoreService,
    private translate: TranslateService,
    private toast: ToastService,
    public mainStateService: MainStateService
  ) { }

  ngOnInit() {
    this.$results.set({ type: "History", entities: this.storage.getArray<Search>(_.RECENT_SEARCHES) });
  }

  async hideSearchbar() {
    this.mainStateService.setSearchPanelVisible(false);
    this.value = "";
  }

  async handleChange(evt: SearchbarCustomEvent) {
    await this.search(evt.detail.value ?? "");
  }

  async searchByKeyword(evt: SearchbarCustomEvent) {
    if (evt.type === "search") {
      // `value` è undefined finché l'utente non digita: "cerca" su barra intatta lanciava
      await this.search((this.value ?? '').trim())
    }
  }

  /** Icona per tipo di luogo. Sconosciuto = il segnaposto generico. */
  private iconFor(type: string): string {
    switch (type) {
      case "motorway_junction": return "car-outline";
      case "bus_stop": return "bus-outline";
      case "station": return "train-outline";
      case "supermarket": return "cart-outline";
      default: return "location-outline";
    }
  }

  /**
   * Risposta del geocoder → voci del pannello: scarta i risultati poco rilevanti, ordina per
   * rilevanza, deduplica. Pura e senza I/O apposta: è la sola parte con logica vera, e così è
   * verificabile senza tirare su né HTTP né Ionic.
   */
  toSearchItems(rv: SearchResult[]): Search[] {
    const bestResults = (rv ?? []).filter(el => el.importance >= TOLLERANCE);
    bestResults.sort((a, b) => b.importance - a.importance);

    const mappedResults = bestResults.map(searchElement => {
      const cuttedSearch = searchElement.display_name.split(',');
      const firstElement = cuttedSearch.shift();
      const searchItem: Search = {
        name: searchElement.display_name,
        title: firstElement,
        subtitle: cuttedSearch.join(', '),
        position: {
          lat: parseFloat(searchElement.lat),
          lng: parseFloat(searchElement.lon),
          showLocationMarker: true,
        },
        icon: this.iconFor(searchElement.type)
      };
      return searchItem;
    });

    return mappedResults.filter(
      (obj, index, self) =>
        index === self.findIndex(
          (t) => t.title === obj.title &&
            t.name === obj.name &&
            t.icon === obj.icon
        )
    );
  }

  /**
   * Il geocoder è un servizio ESTERNO: può essere giù, rispondere 500 o non rispondere affatto.
   * Prima la chiamata era nuda e l'eccezione risaliva fino al gestore d'evento di Angular: il
   * pannello restava esattamente com'era, senza risultati e senza spiegazioni — mentre lo zero
   * risultati, che è un esito MOLTO meno grave, il suo messaggio ce l'aveva già. Ora il fallimento
   * ha il suo stato (`Error`), lo dice nel pannello e lo dice col toast, come le altre operazioni
   * dell'app che possono non riuscire.
   */
  async search(txt: string) {
    if (!txt) {
      this.searchSeq++; // annulla una ricerca in volo: l'utente ha svuotato il campo
      this.$searching.set(false);
      this.$results.set({ type: "History", entities: this.storage.getArray<Search>(_.RECENT_SEARCHES) });
      return;
    }

    const seq = ++this.searchSeq;
    this.$searching.set(true);
    try {
      // `encodeURIComponent`: una `&` o una `#` nel testo cercato spezzavano la query string.
      const rv = await this.HIO.externalGet<SearchResult[]>(`${URL}${encodeURIComponent(txt)}`);
      if (seq !== this.searchSeq) return;
      this.$results.set({ type: "Search", entities: this.toSearchItems(rv) });
    } catch (error) {
      console.log('🐱️ : error', error);
      if (seq !== this.searchSeq) return;
      this.$results.set({ type: "Error", entities: [] });
      await this.toast.presentBasicToast({
        message: this.translate.instant('APP.FILTER.SEARCH_ERROR'),
        color: 'danger',
        icon: 'warning',
      });
    } finally {
      if (seq === this.searchSeq) this.$searching.set(false);
    }
  }


  /**
   *
   * @param search
   * Questo metodo viene chiamato dal template, all'ascolto di un evento del componente figlio recent-$searches
   */
  moveMapToResult(search: Search) {
    this.ionSearchBar.value = search.name;
    this.showPosition.emit({ lat: search.position.lat, lng: search.position.lng, showLocationMarker: search.position.showLocationMarker });
    this.mainStateService.setSearchPanelVisible(false);
    this.setNewRecentSearch(search);
  }

  /**
   *
   * @param search
   * Questo metodo viene usato per gestire il localstorage delle ricerche recenti durante la ricerca
   */
  setNewRecentSearch(search: Search) {
    let localStorageSearches: Search[] = [];

    localStorageSearches = this.storage.getArray<Search>(_.RECENT_SEARCHES);

    // controllo se l'elemento esiste già nella cronologia
    const idxExistingItem = localStorageSearches.findIndex(el => el.name === search.name);;

    if (idxExistingItem !== -1)
      localStorageSearches.splice(idxExistingItem, 1);

    if (localStorageSearches.length >= LOCALSTORAGE_MAX_LENGTH) {
      localStorageSearches.pop();
      localStorageSearches.unshift(search);
    } else {
      localStorageSearches.unshift(search);
    }

    this.storage.set(_.RECENT_SEARCHES, localStorageSearches);
  }

  removeRecentSearch(i: number) {
    let localStorageSearches = this.storage.getArray<Search>(_.RECENT_SEARCHES);
    localStorageSearches.splice(i, 1);
    this.storage.set(_.RECENT_SEARCHES, localStorageSearches)
    this.$results.set({ type: "History", entities: localStorageSearches || [] });
  }

  trackByFn(i: number) {
    return i;
  }
}
